//components/categories.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchCategories, API_BASE_URL } from "../services/api";
import "../components/loading.css";
import "./Categories.css";

function Categories() {
  const [categories, setCategories] = useState([]);
  const [categoryImages, setCategoryImages] = useState({});
  const [imagesLoading, setImagesLoading] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;
    const MIN_SPINNER_MS = 2000;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    (async () => {
      const sleepP = sleep(MIN_SPINNER_MS);
      const fetchP = fetchCategories();

      try {
        const data = await fetchP;
        if (isMounted) {
          const categoryData = Array.isArray(data) ? data : [];
          setCategories(categoryData);
          
          // Initialize loading states
          const loadingStates = {};
          const imageUrls = {};
          
          categoryData.forEach(cat => {
            loadingStates[cat.Id] = true;
            // ✅ Set image URL immediately - no preloading needed
            imageUrls[cat.Id] = `${API_BASE_URL}/category-images/${encodeURIComponent(cat.Name)}`;
          });
          
          setImagesLoading(loadingStates);
          setCategoryImages(imageUrls);
          
          // ✅ Simple approach: just clear loading states after a short delay
          // The browser will handle image loading naturally
          setTimeout(() => {
            if (isMounted) {
              const clearedStates = {};
              categoryData.forEach(cat => {
                clearedStates[cat.Id] = false;
              });
              setImagesLoading(clearedStates);
            }
          }, 500);
        }
      } catch (err) {
        console.error("❌ Error fetching categories:", err);
        if (isMounted) setCategories([]);
      } finally {
        await sleepP;
        if (isMounted) setLoading(false);
      }
    })();

    return () => { isMounted = false; };
  }, []);

  // The build-up view keys on the category NAME (calculated_QUMF_UPDATED.QuantityCatId =
  // the parent/own category Name), not the id — so navigate by name. (Post Phase-0B the id is
  // an opaque UUID; passing it returned 0 rows and showed the UUID as the page title.)
  const handleButtonClick = (quantityCatName) => {
    console.log("🔹 Button clicked for category:", quantityCatName);
    navigate(`/build-up/${encodeURIComponent(quantityCatName)}`);
  };

  // Filter categories based on search term
  const filteredCategories = categories.filter(category =>
    category.Name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="supplier-dashboard-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading Categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="categories-container">
      <div className="categories-header">
        <h1>Categories</h1>
        <div className="search-bar-container">
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="category-search-input"
          />
          {searchTerm && (
            <button 
              className="clear-search-btn"
              onClick={() => setSearchTerm("")}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {filteredCategories.length === 0 ? (
        <div className="no-results">
          <p>No categories found matching "{searchTerm}"</p>
        </div>
      ) : (
        <div className="categories-grid">
          {filteredCategories.map((category) => (
            <div 
              key={category.Id} 
              className="category-card"
              data-category-id={category.Id}
              data-category-name={category.Name}
              style={{
                backgroundImage: categoryImages[category.Id] 
                      ? category.Name.includes('EPD Only')
                        ? `linear-gradient(rgba(34, 197, 94, 0.2), rgba(16, 185, 129, 0.3)), url("${categoryImages[category.Id]}")` // ✅ Green gradient for EPD
                        : `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.6)), url("${categoryImages[category.Id]}")` // Normal black gradient
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
              onClick={() => handleButtonClick(category.Name)}
            >
              {imagesLoading[category.Id] && (
                <div className="image-loading-overlay">
                  <div className="small-spinner"></div>
                </div>
              )}
              <div className="category-content">
                <h3>{category.Name}</h3>
                <button className="view-button">View Details</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Categories;
